import React, {useState} from "react"
import { Form, Row, Col, Button, Popover, OverlayTrigger, Table } from "react-bootstrap"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { formatCurrency } from './utils'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'

const ModelInput = (props) => {
  const [validated, setValidated] = useState(false)

  const {onSubmit} = props

  const currentMrrInfo = (
    <Popover>
      <Popover.Title as='h3'>Monthly Reccuring Revenue</Popover.Title>
      <Popover.Content>
        Your current monthly reccuring revenue. In this model for simplicity of calculations we assume that it stays constant for 12 months straight.
      </Popover.Content>
    </Popover>
  )

  const currentMonthlyChurnInfo = (
    <Popover>
      <Popover.Title as='h3'>Monthly churn</Popover.Title>
      <Popover.Content>
        How much % of your MRR you are losing on monthly basis.
      </Popover.Content>
    </Popover>
  )

  const handleSubmit = event => {
    const form = event.currentTarget

    event.preventDefault()
    event.stopPropagation()

    let validity = form.checkValidity()
    setValidated(true)
    if (validity === false) {
      return
    }

    const data = {
      startingMrr: parseInt(form.elements.startingMrr.value),
      currentChurn: parseFloat(form.elements.currentChurn.value),
    }

    onSubmit(data)
  }

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      <Form.Group as={Row} controlId="churnForm.StartingMrr">
        <Form.Label column sm="8">
          Starting MRR
          <OverlayTrigger trigger='hover' placement='top' overlay={currentMrrInfo}>
            <FontAwesomeIcon icon={faInfoCircle} className='ml-1'/>
          </OverlayTrigger>
        </Form.Label>
        <Col sm="4">
          <Form.Control required name="startingMrr" type="numeric" placeholder="10000" defaultValue="300000"/>
          <Form.Control.Feedback type='invalid'>Enter your current MRR</Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Form.Group as={Row} controlId="churnForm.CurrentChurn">
        <Form.Label column sm="8">
          Current monthly churn %
          <OverlayTrigger trigger='hover' placement='top' overlay={currentMonthlyChurnInfo}>
            <FontAwesomeIcon icon={faInfoCircle} className='ml-1'/>
          </OverlayTrigger>
        </Form.Label>
        <Col sm="4">
          <Form.Control required name="currentChurn" type="number" min="0" max='100' step='0.1' placeholder="5" defaultValue="5"/>
          <Form.Control.Feedback type='invalid'>Current monthly churn. Percent. ex 5.8</Form.Control.Feedback>
        </Col>
      </Form.Group>
      <Button variant="primary" type="submit">
        Calculate Impact
      </Button>
    </Form>
  )
}

const ModelDetailsTable = (props) => {
  const {currentChurn, mrr, loss} = props

  const yearlyChurn = () => {
    return Number.parseFloat(loss[loss.length - 1] * 100 / mrr[0]).toFixed(1)
  }

  return (<>
    {mrr.length > 0 && <p>Yearly churn is <big><b>{`${yearlyChurn()}%`}</b></big>. Your have to add <big><b>{formatCurrency(loss[loss.length - 1])}</b></big> over 12 month to your MRR just to break even at the end of the year.</p>}
    <Table responsive>
      <thead>
        <tr>
          <th></th>
          {mrr.map((value, i) =>
            <th>{`Month ${i+1}`}</th>
           )}
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th>MRR</th>
          {mrr.map((value, i) =>
            <td>
              {formatCurrency(value)}
            </td>
           )}
        </tr>
        <tr>
          <th>Compound loss from {currentChurn}% churn</th>
          {loss.map((value, i) =>
            <td>
              {formatCurrency(value)}
            </td>
           )}
        </tr>
      </tbody>
    </Table>
  </>)
}

const ImpactChart = (props) => {
  const {data, currentChurn} = props

  const formateer = (value, name, props) => {
    return [formatCurrency(value), name]
  }

  const labelFormatter = (label) => {
    return `Total loss on ${label}`
  }

  return (
    <ResponsiveContainer width="90%" height={400} margin={{ top: 20, bottom: 20 }}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" padding={{ left: 30, right: 30 }} />
        <YAxis />
        <Tooltip formatter={formateer} labelFormatter={labelFormatter}/>
        <Legend />
        <Line name={`Monthly churn ${currentChurn}%`} type="monotone" dataKey="lostCurrent" stroke="#8884d8" activeDot={{ r: 6 }} />
      </LineChart>
    </ResponsiveContainer>
  )
}

class MonthlyToYearlyChurnImpact extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      mrr: [],
      loss: []
    }
  }

  componentDidMount = () => {
    this.calculateImpact(
      {
        startingMrr: 300000,
        currentChurn: 5
      }
    )
  }

  calculateImpact = (data) => {
    const numOfMonth = 12
    const {startingMrr, currentChurn} = data
    this.startingMrr = startingMrr
    this.currentChurn = currentChurn

    // let mrr=[startingMrr - startingMrr * currentChurn / 100]
    let mrr=[startingMrr]
    let loss = [startingMrr * currentChurn / 100]
    for (let i = 1; i < numOfMonth; i++) {
      mrr.push(mrr[i-1] - mrr[i-1] * currentChurn / 100)
      loss.push(loss[i-1] + mrr[i] * currentChurn / 100)
    }

    this.setState({mrr, loss})
  }

  convertToLineChartData = (loss) => {
    let data = []
    for (let step = 0; step < 12; step++) {
      data.push(
        {
          month: `Month ${step + 1}`,
          lostCurrent: loss[step]
        }
      )
    }

    return data
  }

  render() {
    const {mrr, loss} = this.state

    return (<>
      <Row className="justify-content-md-center mb-5">
        <Col className="justify-content-md-center">
          <h1 className='text-center mb-3'><small>Monthly to Yearly churn conversion</small></h1>
          <p class='lead'>Did you know that 5% of your monthly churn equals to about 45% of yearly churn? To simplify things a bit we assume that your starting MRR is not being changed over the course of 12 months.</p>
        </Col>
      </Row>
      <Row className="justify-content-md-center">
        <Col className="justify-content-md-center sm-mb-5" sm='12' md='12' lg='5'>
          <ModelInput onSubmit={this.calculateImpact}/>
        </Col>
        <Col sm='12' md='12' lg='7'>
          <ImpactChart data={this.convertToLineChartData(loss)} currentChurn={this.currentChurn}/>
        </Col>
      </Row>
      <Row className="justify-content-md-center mt-5">
        <Col>
          <ModelDetailsTable mrr={mrr} loss={loss} currentChurn={this.currentChurn}/>
        </Col>
      </Row>
    </>
    )

  }
}

export default MonthlyToYearlyChurnImpact
